import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  TextField,
  Select,
  Checkbox,
  Divider,
  SkeletonBodyText,
  Box,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import { orderNumber } from '@/utils/orderNumber';

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Ready to Ship', value: 'ready_to_ship' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'In Use', value: 'in_use' },
  { label: 'Extended', value: 'extended' },
  { label: 'Returned', value: 'returned' },
  { label: 'Complete', value: 'complete' },
];

const STATUS_TONE: Record<string, 'success' | 'warning' | 'critical' | 'info' | undefined> = {
  pending: undefined,
  processing: 'info',
  ready_to_ship: 'info',
  shipped: 'info',
  in_use: 'success',
  extended: 'warning',
  returned: 'warning',
  complete: 'success',
  cancelled: 'critical',
  overdue: 'critical',
};

function formatStatus(status: string) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface OrderHistoryEntry {
  date: string;
  comment: string;
  status: string;
  customerNotified: boolean;
}

export default function ClinicOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const queryClient = useQueryClient();

  const [historyStatus, setHistoryStatus] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [comment, setComment] = useState('');
  const [denyReason, setDenyReason] = useState('');
  const [denyingRequestId, setDenyingRequestId] = useState<string | null>(null);

  const { data: rental, isLoading, error } = useQuery({
    queryKey: ['clinic-order-detail', id],
    queryFn: () => clinicPortalApi.getOrderDetail(id!),
    enabled: !!id,
  });

  // Only fetch available serials when clinic is about to mark as ready_to_ship
  const { data: availableSerialsData } = useQuery({
    queryKey: ['clinic-order-available-serials', id],
    queryFn: () => clinicPortalApi.getAvailableSerials(id!),
    enabled: !!id && historyStatus === 'ready_to_ship',
  });

  const availableSerialOptions = (availableSerialsData?.items || []).flatMap((it) =>
    it.availableSerials.map((s) => ({ label: `${s} (${it.productName})`, value: s })),
  );

  const addHistoryMutation = useMutation({
    mutationFn: (data: { status: string; serialNumber?: string; notifyCustomer?: boolean; comment?: string }) =>
      clinicPortalApi.addOrderHistory(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-order-detail', id] });
      setHistoryStatus('');
      setSerialNumber('');
      setNotifyCustomer(false);
      setComment('');
    },
  });

  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['clinic-pending-requests'],
    queryFn: () => clinicPortalApi.getPendingRequests(),
  });

  const rentalPendingRequests = pendingRequests.filter((r) => r.rentalId === id);

  const approveRequestMutation = useMutation({
    mutationFn: (historyId: string) =>
      clinicPortalApi.approveExtensionRequest(id!, historyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-pending-requests'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-order-detail', id] });
      setDenyingRequestId(null);
      setDenyReason('');
    },
  });

  const denyRequestMutation = useMutation({
    mutationFn: ({ historyId, reason }: { historyId: string; reason?: string }) =>
      clinicPortalApi.denyExtensionRequest(id!, historyId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-pending-requests'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-order-detail', id] });
      setDenyingRequestId(null);
      setDenyReason('');
    },
  });

  const handleAddHistory = useCallback(() => {
    if (!historyStatus) return;
    addHistoryMutation.mutate({
      status: historyStatus,
      serialNumber: serialNumber || undefined,
      notifyCustomer,
      comment: comment || undefined,
    });
  }, [historyStatus, serialNumber, notifyCustomer, comment, addHistoryMutation]);

  if (isLoading) {
    return (
      <Page title="Order Detail" backAction={{ content: 'Orders', onAction: () => navigate(`${base}/orders`) }}>
        <Card><SkeletonBodyText lines={10} /></Card>
      </Page>
    );
  }

  if (error || !rental) {
    return (
      <Page title="Order Detail" backAction={{ content: 'Orders', onAction: () => navigate(`${base}/orders`) }}>
        <Banner tone="critical">
          <p>Failed to load order details. Please try again.</p>
        </Banner>
      </Page>
    );
  }

  const shippingAddr = rental.shippingAddress;

  // Line items table. Only equipment is a weekly rental; wrap / bilateral-fee /
  // add-on lines are one-time charges, so they show their unit/total price, not
  // a (non-existent) weekly rate × weeks.
  const itemTypeLabel: Record<string, string> = {
    equipment: 'Rental',
    wrap: 'Wrap',
    bilateral_fee: 'Bilateral fee',
    addon: 'Add-on',
  };
  const itemRows = (rental.items || []).map((item) => {
    const isEquip = (item.itemType ?? 'equipment') === 'equipment';
    const unitPrice = isEquip ? Number(item.weeklyRate) : Number(item.unitPrice ?? 0);
    const lineTotal = isEquip
      ? Number(item.weeklyRate) * rental.rentalWeeks * item.quantity
      : Number(item.totalPrice ?? Number(item.unitPrice ?? 0) * item.quantity);
    return [
      item.productName,
      itemTypeLabel[item.itemType ?? 'equipment'] ?? 'Add-on',
      isEquip && rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : '—',
      isEquip && rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : '—',
      String(item.quantity),
      isEquip ? `$${unitPrice.toFixed(2)}/wk` : `$${unitPrice.toFixed(2)}`,
      `$${lineTotal.toFixed(2)}`,
    ];
  });

  // Order history from rental notes or a dedicated field
  const historyEntries: OrderHistoryEntry[] = (rental as unknown as { orderHistory?: OrderHistoryEntry[] }).orderHistory || [];
  const historyRows = historyEntries.map((entry) => [
    new Date(entry.date).toLocaleString(),
    entry.comment || '',
    formatStatus(entry.status),
    entry.customerNotified ? 'Yes' : 'No',
  ]);

  return (
    <Page
      title={`Order ${orderNumber(rental)}`}
      backAction={{ content: 'Orders', onAction: () => navigate(`${base}/orders`) }}
      titleMetadata={
        <Badge tone={STATUS_TONE[rental.rentalStatus]}>
          {formatStatus(rental.rentalStatus)}
        </Badge>
      }
    >
      <BlockStack gap="500">
        {rentalPendingRequests.map((req) => (
          <Banner key={req.id} tone="warning" title="Patient extension request awaiting approval">
            <BlockStack gap="300">
              <Text as="p">
                The patient has requested a{' '}
                <strong>{req.weeksRequested}-week extension</strong>
                {req.requestedAt
                  ? ` on ${new Date(req.requestedAt).toLocaleDateString()}`
                  : ''}
                . Approving will extend the rental and add the extension fee to the
                clinic invoice.
              </Text>
              {denyingRequestId === req.id ? (
                <BlockStack gap="200">
                  <TextField
                    label="Reason for denial"
                    value={denyReason}
                    onChange={setDenyReason}
                    autoComplete="off"
                    placeholder="Optional — will be logged in rental history"
                    multiline={2}
                  />
                  <InlineStack gap="200">
                    <Button
                      tone="critical"
                      onClick={() =>
                        denyRequestMutation.mutate({
                          historyId: req.id,
                          reason: denyReason || undefined,
                        })
                      }
                      loading={denyRequestMutation.isPending}
                    >
                      Confirm denial
                    </Button>
                    <Button
                      onClick={() => {
                        setDenyingRequestId(null);
                        setDenyReason('');
                      }}
                    >
                      Cancel
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <InlineStack gap="200">
                  <Button
                    variant="primary"
                    onClick={() => approveRequestMutation.mutate(req.id)}
                    loading={approveRequestMutation.isPending}
                  >
                    Approve extension
                  </Button>
                  <Button onClick={() => setDenyingRequestId(req.id)}>Deny</Button>
                </InlineStack>
              )}
            </BlockStack>
          </Banner>
        ))}

        {/* Order Summary */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Order Summary</Text>
            <InlineGrid columns={2} gap="400">
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">Clinic</Text>
                <Text as="p" variant="bodyMd">{rental.clinic?.name || 'N/A'}</Text>
              </BlockStack>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">Patient</Text>
                <Text as="p" variant="bodyMd">{rental.patientName || 'N/A'}</Text>
              </BlockStack>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">Order Date</Text>
                <Text as="p" variant="bodyMd">{new Date(rental.createdAt).toLocaleDateString()}</Text>
              </BlockStack>
              <BlockStack gap="200">
                <Text as="p" tone="subdued" variant="bodySm">Payment Type</Text>
                <Text as="p" variant="bodyMd">{rental.billingType === 'clinic_invoice' ? 'Clinic Invoice' : 'Immediate'}</Text>
              </BlockStack>
            </InlineGrid>
          </BlockStack>
        </Card>

        {/* Return labels are generated by HealFaster admin, not by clinics. */}

        {/* Addresses */}
        <InlineGrid columns={2} gap="400">
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Payment Address</Text>
              {rental.clinic?.address ? (
                <>
                  <Text as="p" variant="bodyMd">{rental.clinic.address.address1}</Text>
                  {rental.clinic.address.address2 && <Text as="p" variant="bodyMd">{rental.clinic.address.address2}</Text>}
                  <Text as="p" variant="bodyMd">
                    {rental.clinic.address.city}, {rental.clinic.address.province} {rental.clinic.address.zip}
                  </Text>
                  <Text as="p" variant="bodyMd">{rental.clinic.address.country}</Text>
                </>
              ) : (
                <Text as="p" tone="subdued" variant="bodyMd">No billing address on file</Text>
              )}
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Shipping Address</Text>
              {shippingAddr ? (
                <>
                  {(shippingAddr.firstName || shippingAddr.lastName) && (
                    <Text as="p" variant="bodyMd">{`${shippingAddr.firstName || ''} ${shippingAddr.lastName || ''}`.trim()}</Text>
                  )}
                  <Text as="p" variant="bodyMd">{shippingAddr.address1}</Text>
                  {shippingAddr.address2 && <Text as="p" variant="bodyMd">{shippingAddr.address2}</Text>}
                  <Text as="p" variant="bodyMd">
                    {shippingAddr.city}, {shippingAddr.province} {shippingAddr.zip}
                  </Text>
                  <Text as="p" variant="bodyMd">{shippingAddr.country}</Text>
                </>
              ) : (
                <Text as="p" tone="subdued" variant="bodyMd">No shipping address</Text>
              )}
            </BlockStack>
          </Card>
        </InlineGrid>

        {/* Line Items */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Line Items</Text>
            {itemRows.length > 0 ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric']}
                headings={['Product Name', 'Type', 'Start Date', 'End Date', 'Qty', 'Unit Price', 'Total']}
                rows={itemRows}
              />
            ) : (
              <Text as="p" tone="subdued" variant="bodyMd">No line items</Text>
            )}
          </BlockStack>
        </Card>

        {/* Pricing Summary */}
        <Card>
          <BlockStack gap="200">
            <Text as="h2" variant="headingMd">Pricing Summary</Text>
            <InlineStack align="space-between">
              <Text as="p" variant="bodyMd">Sub-Total</Text>
              <Text as="p" variant="bodyMd">${Number(rental.subtotal).toFixed(2)}</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="p" variant="bodyMd">Tax</Text>
              <Text as="p" variant="bodyMd">${Number(rental.taxAmount).toFixed(2)}</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="p" variant="bodyMd">Shipping</Text>
              <Text as="p" variant="bodyMd">${Number(rental.shippingAmount).toFixed(2)}</Text>
            </InlineStack>
            <Divider />
            <InlineStack align="space-between">
              <Text as="p" variant="headingMd" fontWeight="bold">Total</Text>
              <Text as="p" variant="headingMd" fontWeight="bold">${Number(rental.totalAmount).toFixed(2)}</Text>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* Order History */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Order History</Text>
            {historyRows.length > 0 ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Date', 'Comment', 'Status', 'Customer Notified']}
                rows={historyRows}
              />
            ) : (
              <Text as="p" tone="subdued" variant="bodyMd">No history entries yet</Text>
            )}
          </BlockStack>
        </Card>

        {/* Add History Form */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Add History</Text>

            {addHistoryMutation.isError && (
              <Banner tone="critical">
                <p>Failed to add history entry. Please try again.</p>
              </Banner>
            )}

            {addHistoryMutation.isSuccess && (
              <Banner tone="success">
                <p>History entry added successfully.</p>
              </Banner>
            )}

            <Select
              label="Order Status"
              options={[{ label: 'Select status...', value: '' }, ...STATUS_OPTIONS]}
              value={historyStatus}
              onChange={setHistoryStatus}
            />

            {historyStatus === 'ready_to_ship' && (
              <Select
                label="Product Serial No."
                options={[
                  { label: availableSerialOptions.length > 0 ? 'Select serial...' : 'No serials available in warehouse', value: '' },
                  ...availableSerialOptions,
                ]}
                value={serialNumber}
                onChange={setSerialNumber}
                helpText="Only warehouse-ready serials for this order's SKUs are shown"
              />
            )}

            <Checkbox
              label="Notify Customer"
              checked={notifyCustomer}
              onChange={setNotifyCustomer}
            />

            <TextField
              label="Comment"
              value={comment}
              onChange={setComment}
              multiline={4}
              autoComplete="off"
            />

            <Box>
              <Button
                variant="primary"
                onClick={handleAddHistory}
                loading={addHistoryMutation.isPending}
                disabled={!historyStatus}
              >
                Update Order
              </Button>
            </Box>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
